<template>
  <div
    ref="tableContainer"
    class="table-container"
  >
    <Spin
      v-show="loading"
      fix
    />
    <div
      v-show="!loading"
      class="ivu-table ivu-table-small"
      :class="{ 'ivu-table-border': border, 'ivu-table-stripe': stripe }"
    >
      <div
        ref="table"
        class="ivu-table-body ivu-table-overflowX ivu-table-overflowY"
      >
        <table
          v-show="data.length"
          cellspacing="0"
          cellpadding="0"
          border="0"
        >
          <thead
            ref="tableHead"
            class="ivu-table-thead"
          >
            <tr v-if="groupBy">
              <th
                v-if="additionalColumns.length"
                rowspan="2"
                :style="{ width: `${tableHeaderAdditionalLeftColWidth}px`, minWidth: `${tableHeaderAdditionalLeftColWidth}px`, maxWidth: `${tableHeaderAdditionalLeftColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
              <th
                rowspan="2"
                :style="{ width: `${tableHeaderLeftColWidth}px`, minWidth: `${tableHeaderLeftColWidth}px`, maxWidth: `${tableHeaderLeftColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
              <th
                v-for="col in tableHeaderRow"
                :key="`custom-table-header-additional-row-${col.name}`"
                rowspan="1"
                :colspan="col.occurence"
                class="ivu-table-column-center"
                :style="{
                  borderLeft: `1px solid #e8eaec`,
                  padding: '0 5px',
                  width: `${col.occurence * tableColWidth}px`,
                  maxWidth: `${col.occurence * tableColWidth}px`,
                  minWidth: `${col.occurence * tableColWidth}px`
                }"
              >
                {{ col.name }}
              </th>
              <th
                v-if="checkChart()"
                :style="{ width: `${tableHeaderRightColWidth}px`, minWidth: `${tableHeaderRightColWidth}px`, maxWidth: `${tableHeaderRightColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
            </tr>
            <tr>
              <th
                v-if="!groupBy && additionalColumns.length"
                rowspan="1"
                :style="{ width: `${tableHeaderAdditionalLeftColWidth}px`, minWidth: `${tableHeaderAdditionalLeftColWidth}px`, maxWidth: `${tableHeaderAdditionalLeftColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
              <th
                v-if="!groupBy"
                rowspan="1"
                colspan="1"
                :style="{ width: `${tableHeaderLeftColWidth}px`, minWidth: `${tableHeaderLeftColWidth}px`, maxWidth: `${tableHeaderLeftColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
              <th
                v-for="(row, index) in copyOfData"
                :key="`custom-table-header-th-${index}`"
                rowspan="1"
                colspan="1"
                class="ivu-table-column-center"
                :style="{
                  borderLeft: tableBodyBorderPosition[index] ? '1px solid #e8eaec': 'none',
                  width: `${tableColWidth}px`,
                  minWidth: `${tableColWidth}px`,
                  maxWidth: `${tableColWidth}px`
                }"
                @mouseenter="$event => handleShowHeaderTooltip($event, row[headerColumn.name], additionalHeaderColumn ? row[additionalHeaderColumn.name] : null)"
                @mouseleave="handleCloseHeaderTooltip"
              >
                <template v-if="numberTypes.includes(headerColumn.format)">
                  {{ formatNumber(row[headerColumn.name], headerColumn.format, headerColumn.precision) }}
                </template>
                <template v-else-if="['date'].includes(headerColumn.format)">
                  {{ $moment(row[headerColumn.name], headerColumn.fromFormat).format(headerColumn.toFormat) }}
                </template>
                <template v-else>
                  {{ row[headerColumn.name] }}
                </template>
              </th>
              <th
                v-if="checkChart()"
                :style="{ width: `${tableHeaderRightColWidth}px`, minWidth: `${tableHeaderRightColWidth}px`, maxWidth: `${tableHeaderRightColWidth}px` }"
                class="ivu-table-column-center ivu-table-hidden"
              />
            </tr>
          </thead>
          <tbody
            ref="tableBody"
            class="ivu-table-tbody"
            :style="{ height: `${tableBodyHeight}px` }"
            @scroll.passive="updateSyncedScroll"
          >
            <CustomTableTrHorizontal
              v-for="(column, index) in Object.keys(columns)"
              :key="`custom-table-body-tr-${index}`"
              :column="{ ...columns[column], name: column }"
              :data="copyOfData"
              :left-width="tableHeaderLeftColWidth"
              :col-width="tableColWidth"
              :col-min-width="tableColMinWidth"
              :min-width-factor="minWidthFactor"
              :additional-header-column="additionalHeaderColumn"
              :header-column="headerColumn"
              :additional-width="additionalColumns.length ? tableHeaderAdditionalLeftColWidth : 0"
              :table-body-border-position="tableBodyBorderPosition"
              :value-change-map="valueChangeMap"
            />
          </tbody>
        </table>
      </div>
      <div
        v-show="data.length"
        :style="{
          height: '100%',
          width: `${tableHeaderLeftColWidth + (additionalColumns.length ? tableHeaderAdditionalLeftColWidth : 0)}px`,
          zIndex: 2
        }"
        class="ivu-table-fixed"
      >
        <div
          class="ivu-table-fixed-header"
        >
          <table
            v-if="additionalColumns.length"
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{
              height: `${tableHeadHeight}px`,
              width: `${tableHeaderAdditionalLeftColWidth}px`,
              float: 'left'
            }"
          >
            <thead>
              <tr>
                <th
                  :style="{
                    padding: 0,
                    height: groupBy ? '60px' : '30px',
                    width: `${tableHeaderAdditionalLeftColWidth}px`,
                    minWidth: `${tableHeaderAdditionalLeftColWidth}px`,
                    maxWidth: `${tableHeaderAdditionalLeftColWidth}px`
                  }"
                  class="ivu-table-column-center"
                />
              </tr>
            </thead>
          </table>
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
          >
            <thead>
              <tr>
                <th
                  :style="{
                    padding: 0,
                    height: `${tableHeadHeight}px`,
                    width: `${tableHeaderLeftColWidth}px`,
                    minWidth: `${tableHeaderLeftColWidth}px`,
                    maxWidth: `${tableHeaderLeftColWidth}px`
                  }"
                  class="ivu-table-column-center"
                />
              </tr>
            </thead>
          </table>
        </div>
        <div
          class="ivu-table-fixed-body"
        >
          <table
            v-if="additionalColumns.length"
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{
              width: `${tableHeaderAdditionalLeftColWidth}px`,
              float: 'left'
            }"
          >
            <tbody class="ivu-table-tbody">
              <tr
                v-for="(column, index) in additionalColumns"
                :key="`custom-table-fixed-body-tr-additional-${index}`"
                class="ivu-table-row"
              >
                <td
                  :style="[{
                    height: `${column.span * 30}px`,
                    width: `${tableHeaderAdditionalLeftColWidth}px`,
                    minWidth: `${tableHeaderAdditionalLeftColWidth}px`,
                    maxWidth: `${tableHeaderAdditionalLeftColWidth}px`
                  }, column.styles]"
                  class="ivu-table-column-center"
                >
                  <div class="ivu-table-cell-additional-column">
                    {{ column.name }}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
          >
            <tbody
              ref="fixedLeftTableBody"
              class="ivu-table-tbody"
              :style="{
                maxHeight: `${tableBodyHeight}px`,
                width: `${tableHeaderLeftColWidth}px`,
                float: 'left',
                overflowY: 'hidden'
              }"
            >
              <CustomTableTrFixedColumn
                v-for="(column, index) in Object.keys(columns)"
                :key="`custom-table-body-tr-${index}`"
                :column="Object.assign(columns[column], { name: column })"
                :width="tableHeaderLeftColWidth"
                :indent-size="indentSize"
                :path="[]"
              />
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-if="checkChart()"
        :style="{
          width: `${chart.width}px`
        }"
        class="ivu-table-fixed-right"
        style="right: 0px"
      >
        <div
          class="ivu-table-fixed-header"
        >
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{ width: `${chart.width}px` }"
          >
            <thead>
              <tr>
                <th
                  :style="{
                    padding: 0,
                    width: `${chart.width}px`,
                    minWidth: `${chart.width}px`,
                    maxWidth: `${chart.width}px`
                  }"
                  class="ivu-table-column-center"
                />
              </tr>
            </thead>
          </table>
        </div>
        <div class="ivu-table-fixed-body">
          <table
            cellspacing="0"
            cellpadding="0"
            border="0"
            :style="{width: `${chart.width}px`}"
          >
            <tbody
              ref="fixedRightTableBody"
              class="ivu-table-tbody ivu-table-overflowX"
              :style="{
                maxHeight: `${tableBodyHeight}px`,
                float: 'left',
                overflow: 'hidden'
              }"
            >
              <CustomTableTrFixedChart
                v-for="(column, index) in Object.keys(columns)"
                :key="`custom-table-fixed-body-tr-${index}`"
                :column="Object.assign(columns[column], { name: column })"
                :data="copyOfData"
                :chart="chart"
                :header-column="headerColumn"
                :depth="0"
              />
            </tbody>
          </table>
        </div>
      </div>
      <div
        v-show="!data.length"
        class="ivu-table-tip"
      >
        <table
          cellspacing="0"
          cellpadding="0"
          border="0"
        >
          <tbody>
            <tr>
              <td style="width: 1px;">
                {{ noDataText }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <!-- header tooltip -->
    <CustomTooltip
      :show-tooltip="showContextTooltip"
      :tooltip-top="headerTooltipConfig.top"
      :tooltip-left="headerTooltipConfig.left"
    >
      <div v-if="selectedHeaderContextData && headerColumn.contextTooltip">
        <div
          v-if="additionalHeaderColumn"
          class="context-tooltip-row"
        >
          {{ additionalHeaderColumn.displayName || additionalHeaderColumn.name }}: {{ formatHeaderContextValue(headerTooltipConfig.additionalHeaderValue, additionalHeaderColumn) }}
        </div>
        <div class="context-tooltip-row">
          {{ headerColumn.contextTooltipDisplayName || headerColumn.displayName || headerColumn.name }}: {{ formatHeaderContextValue(headerTooltipConfig.headerValue, headerColumn) }}
        </div>
        <template v-for="(tooltip, index) in headerColumn.contextTooltip">
          <div
            :key="index"
            class="context-tooltip-row"
          >
            <div v-if="tooltip.chart">
              <div v-if="tooltip.displayName">
                {{ tooltip.displayName }}:
              </div>
              <div style="height: 100px; width: 400px">
                <LineChart
                  :data="computeLineChartData(selectedHeaderContextData[tooltip.key], tooltip.chart)"
                  :margin="{ l: 5, r: 5, t: 5, b: 5 }"
                  :x-axis="tooltip.chart.xAxis ? {
                    ...tooltip.chart.xAxis,
                    zeroline: false
                  } : null"
                  :y-axis="tooltip.chart.yAxis ? {
                    nonNegative: tooltip.chart.yAxis.nonNegative,
                    zeroline: false
                  } : {
                    zeroline: false
                  }"
                  hide-toolbar
                  :show-legend="tooltip.chart.dataTraces.length > 1"
                  style="height:100%;width:100%"
                />
              </div>
            </div>
            <div v-else>
              {{ tooltip.displayName }}: {{ selectedHeaderContextData ? formatHeaderContextValue(selectedHeaderContextData[tooltip.key], tooltip) : '--' }}
            </div>
          </div>
        </template>
      </div>
    </CustomTooltip>
    <!-- cell tooltip -->
    <CustomTooltip
      :show-tooltip="hasTooltip && showTooltip"
      :tooltip-top="tooltipTop"
      :tooltip-left="tooltipLeft"
    >
      <div v-if="hasTooltip">
        <p v-if="additionalHeaderColumn">
          {{ additionalHeaderColumn.displayName }}: {{ formatCellByPath(selectedRow, [additionalHeaderColumn.name], additionalHeaderColumn.format, additionalHeaderColumn.fromFormat, additionalHeaderColumn.toFormat) }}
        </p>
        <p>
          {{ headerColumn.displayName }}: {{ formatCellByPath(selectedRow, [headerColumn.name], headerColumn.format, headerColumn.fromFormat, headerColumn.toFormat) }}
        </p>
        <p>
          {{ columns[selectedColumn].displayName || selectedColumn }}: {{ formatCellByPath(selectedRow, [selectedColumn], columns[selectedColumn].format, columns[selectedColumn].fromFormat, columns[selectedColumn].toFormat) }}
        </p>
        <template v-for="tooltipRow in columns[selectedColumn].tooltip">
          <p
            v-if="tooltipRow.column !== selectedColumn"
            :key="tooltipRow.column"
          >
            {{ tooltipRow.displayName || tooltipRow.column }}: {{ formatCellByPath(selectedRow, [tooltipRow.column], tooltipRow.format, tooltipRow.fromFormat, tooltipRow.toFormat) }}
          </p>
        </template>
      </div>
    </CustomTooltip>
  </div>
</template>

<script>
import _ from 'lodash'
import CustomTableMixin from '@/mixins/custom-table-mixin'
import CustomTableTrHorizontal from './CustomTableTrHorizontal'
import CustomTableTrFixedColumn from './CustomTableTrFixedColumn'
import CustomTableTrFixedChart from './CustomTableTrFixedChart'
import CustomTooltip from './CustomTooltip'
import LineChart from '@/components/helper-components/chart-components/LineChart'

export default {
  components: {
    CustomTableTrHorizontal,
    CustomTableTrFixedColumn,
    CustomTableTrFixedChart,
    CustomTooltip,
    LineChart
  },

  mixins: [
    CustomTableMixin
  ],

  data () {
    return {
      headerTooltipConfig: {
        show: false,
        top: 0,
        left: 0,
        headerValue: null,
        additionalHeaderValue: null
      }
    }
  },

  computed: {
    tableColNum () {
      return this.data.length || 1
    },

    showContextTooltip () {
      return this.headerHasTooltip && this.headerTooltipConfig.show
    },

    headerHasTooltip () {
      const { contextTooltip = [] } = this.headerColumn

      return Boolean(contextTooltip.length && this.selectedHeaderContextData)
    },

    selectedHeaderContextData () {
      const { headerValue, additionalHeaderValue } = this.headerTooltipConfig
      const path = additionalHeaderValue ? [additionalHeaderValue, headerValue] : [headerValue]

      return this.headerColumn.contextData && _.get(this.headerColumn.contextData, path)
    }
  },

  watch: {
    data (val) {
      this.copyOfData = _.cloneDeep(val)
      this.scrollTable()
    },

    styles (val) {
      this.tableHeaderAdditionalLeftColWidth = _.get(val, 'additionalLeftColWidth', 30)
      this.tableHeaderLeftColWidth = _.get(val, 'leftColWidth', 200)
      this.tableColMinWidth = _.get(val, 'colMinWidth', 100)
    }
  },

  mounted () {
    this.copyOfData = _.cloneDeep(this.data)
    this.updateSyncedScroll()
    this.scrollTable()
    window.addEventListener('resize', this.calculateTableDimension)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.calculateTableDimension)
  },

  updated () {
    if (!this.loading) {
      this.calculateTableDimension()
    }
  },

  methods: {
    /**
     * Check table width and height
     */
    calculateTableDimension () {
      this.tableWidth = this.$refs.tableContainer.clientWidth
      this.tableHeight = this.maxHeight ? this.maxHeight : this.$refs.tableContainer.clientHeight
      this.tableHeadHeight = this.$refs.tableHead.clientHeight
      this.tableBodyHeight = this.tableHeight - this.tableHeadHeight
    },

    /**
     * Scroll table to farther right
     */
    scrollTable () {
      if (this.scroll && _.has(this.$refs.table, 'scrollTo')) {
        this.$refs.tableBody.scrollTo({
          top: 0,
          left: this.$refs.tableBody.scrollWidth,
          behavior: 'smooth'
        })
      }
    },

    /**
     * Auto scroll table head with table body in case there's overflow
     */
    updateSyncedScroll () {
      const tableBody = this.$refs.tableBody
      const tableHead = this.$refs.tableHead
      const fixedLeftTableBody = this.$refs.fixedLeftTableBody
      const fixedRightTableBody = this.$refs.fixedRightTableBody
      const {
        scrollLeft,
        scrollTop
      } = tableBody
      if (tableHead && tableHead.scrollLeft !== scrollLeft) {
        tableHead.scrollLeft = scrollLeft
      }
      if (fixedLeftTableBody && fixedLeftTableBody.scrollTop !== scrollTop) {
        fixedLeftTableBody.scrollTop = scrollTop
      }
      if (fixedRightTableBody && fixedRightTableBody.scrollTop !== scrollTop) {
        fixedRightTableBody.scrollTop = scrollTop
      }
    },

    handleShowHeaderTooltip ($event, headerValue, additionalHeaderValue = null) {
      this.$set(this.headerTooltipConfig, 'headerValue', headerValue)
      this.$set(this.headerTooltipConfig, 'additionalHeaderValue', additionalHeaderValue)
      this.$set(this.headerTooltipConfig, 'top', $event.pageY + 10)
      this.$set(this.headerTooltipConfig, 'left', $event.pageX + 10)
      this.$set(this.headerTooltipConfig, 'show', true)
    },

    handleCloseHeaderTooltip () {
      this.$set(this.headerTooltipConfig, 'show', false)
    },

    formatHeaderContextValue (value, formatConfig) {
      const { format } = formatConfig

      if (format === 'temperature') {
        return this.formatTemperature(value)
      } else if (format === 'date') {
        const { fromFormat, toFormat } = formatConfig
        return this.$moment(value, fromFormat).format(toFormat)
      } else if (format === 'list-string') {
        const { delimiter, emptyDefault } = formatConfig

        return value.length ? value.join(delimiter) : emptyDefault
      }

      return value
    },

    formatTemperature (value) {
      return typeof value === 'number' ? `${value} \xB0C` : '--'
    },

    computeLineChartData (chartData, chartConfig) {
      const { dataTraces = [] } = chartConfig

      return dataTraces.map(dataTrace => ({
        x: chartData.map(row => row[dataTrace.x]),
        y: chartData.map(row => row[dataTrace.y]),
        color: dataTrace.color,
        name: dataTrace.name
      }))
    }
  }
}
</script>

<style scoped>
  .table-container {
    border-top: 1px solid var(--color-card-border);
    border-left: 1px solid var(--color-card-border);
    position: relative;
    width: 100%;
    height: 100%;
  }
  .ivu-table {
    cursor: pointer;
  }
  .ivu-table-body {
    clear: both;
    padding: 0;
    margin-left: auto;
    margin-right: auto;
  }
  .ivu-table-thead {
    -webkit-box-flex: 0;
    -ms-flex: 0 0 auto;
    flex: 0 0 auto;
    display: block;
    overflow: hidden;
  }
  .ivu-table-tbody {
    display: block;
    -webkit-box-flex: 1;
    -ms-flex: 1 1 auto;
    flex: 1 1 auto;
    overflow: auto;
  }
  .ivu-table-cell {
    padding: 3px 5px;
    word-break: unset;
  }
  .ivu-table-stripe .ivu-table-body tr td:hover {
    background-color: #ebf7ff;
  }
  .ivu-table-small td {
    height: 30px;
  }
  .ivu-table-small th {
    height: 30px;
  }
  .ivu-table-cell-additional-column {
    writing-mode: tb-rl;
    font-weight: bold;
    font-size: 16px;
    -ms-transform: rotate(-180deg); /* IE 9 */
    -webkit-transform: rotate(-180deg); /* Safari 3-8 */
    transform: rotate(-180deg);
  }
  .ivu-table-column-center pre{
    font-family: 'Montserrat', sans-serif;
    margin: 0;
    text-align: left;
    padding-left: 10px;
  }

  .context-tooltip-row {
    margin-bottom: 4px;
  }
</style>
